import {useEffect, useRef} from 'react'
import {useLocation} from 'react-router-dom'
import {splitArrayIntoChunks, calculateIndex} from '../utils/utils'
import {useGoogleTagManager} from '../custom-api/hooks/use-google-tag-manager'
import {item_gtm_event, polish_ecommerce_gtm_event} from '../custom-api/layers/default'
import {getPriceData, getVariantFromRepresentedProduct} from '../utils/product-utils'

export const usePlpGtmEvent = (
    productSearchResult,
    isSearch,
    categoryData,
    pageIsPlpPlus,
    isLoading,
) => {
    const location = useLocation()
    const currentPage = new URLSearchParams(location.search).get('page') || 1
    const {addLayer} = useGoogleTagManager()
    const savedProductsRef = useRef([])
    const isLocationChangeEventSent = useRef(null)
    const internalSearchSentTerm = useRef(null)

    const pageRequested = productSearchResult.page
    const searchTerm = isSearch ? new URLSearchParams(location.search).get('q') : null
    const searchTotal = productSearchResult?.total

    useEffect(() => {
        if (!searchTerm) {
            internalSearchSentTerm.current = null
            return
        }
        if (isLoading || searchTotal === undefined) return
        if (internalSearchSentTerm.current === searchTerm) return

        internalSearchSentTerm.current = searchTerm
        const internalSearchLayer = {
            event: 'internalSearch',
            eventCategory: 'engagement',
            eventAction: 'internalSearch-result',
            eventLabel: searchTerm,
            resultsAmount: searchTotal,
        }
        addLayer(() => internalSearchLayer)
    }, [searchTerm, searchTotal, isLoading])

    useEffect(() => {
        if (currentPage === 1) {
            savedProductsRef.current = []
        }
    }, [currentPage])

    useEffect(() => {
        if (productSearchResult?.hits) {
            const productsToCheck =
                (productSearchResult?.hits?.length ?? 0) > 32
                    ? [
                          ...(productSearchResult?.hits?.slice(0, 16) ?? 0),
                          ...(productSearchResult?.hits?.slice(-16) ?? 0),
                      ]
                    : productSearchResult?.hits

            const productsToSend = productsToCheck.filter(
                (p) => !savedProductsRef?.current?.includes(p.productId),
            )

            if (productsToSend.length > 0) {
                const splitProducts = splitArrayIntoChunks(productsToSend)
                splitProducts.forEach(async (chunk, chunkIndex) => {
                    const offset = chunkIndex === 1 ? splitProducts?.[0]?.length : 0 // Offset for the second chunk
                    await addLayer(polish_ecommerce_gtm_event.bind(this))
                    await addLayer(
                        item_gtm_event.bind(this, {
                            event: currentPage === 1 ? 'view_item_list' : 'view_item_listButton',
                            item_list_name: isSearch ? 'search_result' : categoryData?.name,
                            items: chunk.map((product, i) => {
                                const variant = getVariantFromRepresentedProduct(product)
                                return {
                                    item_name: product?.c_defaultName,
                                    item_id: product?.representedProduct?.id,
                                    item_category: product?.c_defaultCategoryName,
                                    item_variant: product?.productId,
                                    price: +(
                                        variant?.price /
                                        (1 + product?.c_taxRate / 100)
                                    ).toFixed(2),
                                    fullprice: getPriceData(product)?.isOnSale
                                        ? 'reduced'
                                        : 'fullprice',
                                    rating_points: '',
                                    erp_category: product?.representedProduct?.c_materialGroup,
                                    prdha_category: product?.c_prdha_category,
                                    index: calculateIndex(pageRequested, i, offset, pageIsPlpPlus), // Pass offset directly
                                    stock_status:
                                        product?.c_extendVariant?.stock > 0
                                            ? 'in-stock'
                                            : 'out-of-stock',
                                }
                            }),
                            currency: productSearchResult?.hits?.[0]?.currency,
                        }),
                    )
                })

                sendEventLocationChange(isLoading)

                savedProductsRef.current = [
                    ...savedProductsRef.current,
                    ...productsToSend.map((p) => p.productId),
                ]
            }
        } else {
            sendEventLocationChange(isLoading)
        }
    }, [productSearchResult?.hits])

    const sendEventLocationChange = () => {
        if (isLoading) return
        if (isLocationChangeEventSent.current !== location.pathname) {
            setTimeout(() => {
                //console.log('[SFCC] Location change PLP')
                window.dispatchEvent(new Event('locationchange'))
            }, 180)
            isLocationChangeEventSent.current = location.pathname
        }
    }
}
